import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import DashboardLayout from "@/components/DashboardLayout";
import * as kundeApi from "@/api/kunde";
import type { KundeResponseDTO } from "@/api/types";
import { Alert, CircularProgress, Snackbar } from "@mui/material";

interface Customer {
  id: number;
  ikNumber: string;
  name: string;
  benchmarking: boolean;
  benchmarkingBezeichnung: string;
  ort: string;
}

const Customers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    ikNumber: "",
    name: "",
    benchmarking: false,
    benchmarkingBezeichnung: "",
    ort: "",
  });

  const [ikFilter, setIkFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [ortFilter, setOrtFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await kundeApi.getAll();
      setCustomers(data.map(k => ({
        id: k.id,
        ikNumber: k.ik || "",
        name: k.name || "",
        benchmarking: k.benchmarking ?? false,
        benchmarkingBezeichnung: k.benchmarkingBezeichnung || "",
        ort: k.ort || "",
      })));
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Kunden");
      setSnackbar({ open: true, message: "Fehler beim Laden der Kunden", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const matchesIk = !ikFilter || (customer.ikNumber || "").includes(ikFilter);
      const matchesName = !nameFilter || (customer.name || "").toLowerCase().includes(nameFilter.toLowerCase());
      const matchesOrt = !ortFilter || (customer.ort || "").toLowerCase().includes(ortFilter.toLowerCase());
      return matchesIk && matchesName && matchesOrt;
    });
  }, [customers, ikFilter, nameFilter, ortFilter]);

  const paginatedCustomers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredCustomers.slice(start, start + rowsPerPage);
  }, [filteredCustomers, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    setEditingCustomer(null);
    setFormData({ ikNumber: "", name: "", benchmarking: false, benchmarkingBezeichnung: "", ort: "" });
    setIsDialogOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      ikNumber: customer.ikNumber,
      name: customer.name,
      benchmarking: customer.benchmarking,
      benchmarkingBezeichnung: customer.benchmarkingBezeichnung,
      ort: customer.ort,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Möchten Sie diesen Kunden wirklich löschen?")) return;
    
    try {
      await kundeApi.remove(id);
      setCustomers(customers.filter((c) => c.id !== id));
      setSnackbar({ open: true, message: "Kunde erfolgreich gelöscht", severity: "success" });
    } catch (err: any) {
      const errorMsg = err.message || "Fehler beim Löschen des Kunden";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingCustomer) {
        const updated = await kundeApi.update(editingCustomer.id, {
          name: formData.name,
          ik: formData.ikNumber,
          benchmarking: formData.benchmarking,
          benchmarkingBezeichnung: formData.benchmarkingBezeichnung || undefined,
          ort: formData.ort,
        });
        setCustomers(
          customers.map((c) =>
            c.id === editingCustomer.id
              ? { id: updated.id, ikNumber: updated.ik, name: updated.name, benchmarking: updated.benchmarking, benchmarkingBezeichnung: updated.benchmarkingBezeichnung || "", ort: updated.ort || "" }
              : c
          )
        );
        setSnackbar({ open: true, message: "Kunde erfolgreich aktualisiert", severity: "success" });
      } else {
        const created = await kundeApi.create({
          name: formData.name,
          ik: formData.ikNumber,
          benchmarking: formData.benchmarking,
          benchmarkingBezeichnung: formData.benchmarkingBezeichnung || undefined,
          ort: formData.ort,
        });
        const newCustomer: Customer = {
          id: created.id,
          ikNumber: created.ik,
          name: created.name,
          benchmarking: created.benchmarking,
          benchmarkingBezeichnung: created.benchmarkingBezeichnung || "",
          ort: created.ort || "",
        };
        setCustomers([...customers, newCustomer]);
        setSnackbar({ open: true, message: "Kunde erfolgreich erstellt", severity: "success" });
      }
      setIsDialogOpen(false);
    } catch (err: any) {
      const errorMsg = err.message || "Fehler beim Speichern des Kunden";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold">
            Kundenverwaltung
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              size="large"
              sx={{ color: "white" }}
            >
              Neuer Kunde
            </Button>
          </Box>
        </Box>

        {/* Filter */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filter
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
            <TextField
              label="IK-Nummer"
              placeholder="IK suchen..."
              value={ikFilter}
              onChange={(e) => { setIkFilter(e.target.value); setPage(0); }}
              fullWidth
            />
            <TextField
              label="Bezeichnung"
              placeholder="Bezeichnung suchen..."
              value={nameFilter}
              onChange={(e) => { setNameFilter(e.target.value); setPage(0); }}
              fullWidth
            />
            <TextField
              label="Ort"
              placeholder="Ort suchen..."
              value={ortFilter}
              onChange={(e) => { setOrtFilter(e.target.value); setPage(0); }}
              fullWidth
            />
          </Box>
        </Paper>

        {/* Table */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>IK-Nummer</strong></TableCell>
                  <TableCell><strong>Bezeichnung</strong></TableCell>
                  <TableCell><strong>Ort</strong></TableCell>
                  <TableCell><strong>Benchmarking</strong></TableCell>
                  <TableCell><strong>Benchmarking-Bezeichnung</strong></TableCell>
                  <TableCell align="right"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        {customers.length === 0
                          ? "Keine Kunden vorhanden"
                          : "Keine Kunden entsprechen den Filterkriterien"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>{customer.ikNumber}</TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.ort}</TableCell>
                      <TableCell>{customer.benchmarking ? "Ja" : "Nein"}</TableCell>
                      <TableCell>{customer.benchmarkingBezeichnung}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(customer)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(customer.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredCustomers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} von ${count}`
            }
          />
        </Paper>
        )}
      </Box>

      {/* Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCustomer ? "Kunde bearbeiten" : "Neuen Kunden hinzufügen"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="IK-Nummer"
                value={formData.ikNumber}
                onChange={(e) => {
                  const raw = e.target.value;
                  const match = raw.match(/^(\d{0,9})(-(\d{0,2}))?/);
                  const value = match
                    ? match[1] + (match[2] ? "-" + (match[3] ?? "") : "")
                    : "";
                  setFormData({ ...formData, ikNumber: value });
                }}
                required
                fullWidth
                inputProps={{ maxLength: 12 }}
                helperText="9-stellige Nummer, optional mit Standort (z.B. 123456789-1)"
              />
              <TextField
                label="Bezeichnung"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                fullWidth
              />
              <TextField
                label="Ort"
                value={formData.ort}
                onChange={(e) =>
                  setFormData({ ...formData, ort: e.target.value })
                }
                fullWidth
                inputProps={{ maxLength: 100 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.benchmarking}
                    onChange={(e) =>
                      setFormData({ ...formData, benchmarking: e.target.checked })
                    }
                  />
                }
                label="Benchmarking"
              />
              <TextField
                label="Benchmarking-Bezeichnung"
                value={formData.benchmarkingBezeichnung}
                onChange={(e) =>
                  setFormData({ ...formData, benchmarkingBezeichnung: e.target.value })
                }
                fullWidth
                inputProps={{ maxLength: 100 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ color: "white" }}
            >
              {editingCustomer ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Customers;
